import type { JSX } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';
import Html from 'components/html';
import ExpandableRow from 'components/expandable-row';
import { IconList } from 'components/icon';
import Link from 'components/link';
import { sendTracking } from 'services/google-analytics';
import wiki from 'services/wiki';
import { noDigitsCurrencyFormat } from 'utils/format-number';

import tracking from './questions-banner.tracking';
import type { Row } from './questions-banner.settings';
import { testId, rows, PAYOUT_THRESHOLD } from './questions-banner.settings';
import messages from './questions-banner.i18n';
import styles from './questions-banner.module.scss';

const translation = messages.pages.landing.questionsBanner.livejasmin;

const QuestionsBanner: React.FunctionComponent<unknown> = () => {
  const { formatMessage, formatNumber } = useIntl();

  const [expandedId, setExpandedId] = React.useState<string>(rows[0].id);

  const toggleExpanded = React.useCallback(
    (id: string) => () => {
      sendTracking(tracking.toggleQuestion, { id });
      setExpandedId((current) => (current === id ? '' : id));
    },
    []
  );

  const renderAnswer = (row: Row): JSX.Element => {
    if (row.hasList) {
      return (
        <Html.ul className="m-0 px-6">
          {formatMessage(row.answer, {
            li(value) {
              return (
                <Html.li typography="body2" className="m-0">
                  {value}
                </Html.li>
              );
            },
          })}
        </Html.ul>
      );
    }

    return (
      <Html.p typography="body2" className="m-0">
        {formatMessage(row.answer, { threshold: formatNumber(PAYOUT_THRESHOLD, noDigitsCurrencyFormat), minAge: 18 })}
      </Html.p>
    );
  };

  return (
    <Html.div
      className={[
        styles.questionsBanner,
        'd-flex flex-column align-items-center justify-content-center w-100 pt-12 pb-8 px-4 pt-md-20 pb-md-16 px-md-16',
      ]}
      testId={testId.component}
    >
      <Html.h3 className={[styles.title, 'mb-4']} typography={['display3', 'display1-md']} fontWeight="light">
        {formatMessage(translation.title, {
          b: (contents: React.ReactNode) => <Html.b fontWeight="bold">{contents}</Html.b>,
        })}
      </Html.h3>

      <Html.p className={[styles.description, 'mb-6 mb-md-10']} typography="body1">
        {formatMessage(translation.description)}
      </Html.p>

      <Html.div className={[styles.questions, 'mb-6']}>
        {rows.map((row) => (
          <Html.div key={row.id}>
            <ExpandableRow
              className={styles.expandableRow}
              title={formatMessage(row.question)}
              expanded={expandedId === row.id}
              onToggle={toggleExpanded(row.id)}
              toggleIcon={{
                open: IconList.minusOutline,
                closed: IconList.plusOutline,
              }}
            >
              {renderAnswer(row)}
            </ExpandableRow>
          </Html.div>
        ))}
      </Html.div>

      <Html.div className="text-center">
        <Link href={wiki.faq} target="_blank" variant="button-text-primary" typography="body2">
          {formatMessage(translation.moreInfo)}
        </Link>
      </Html.div>
    </Html.div>
  );
};

export default QuestionsBanner;
